import { useState, useEffect } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { ShoppingBag, LogOut, User, Bell, Loader2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Logo } from '@/shared/components/Logo'
import { cn } from '@/shared/utils'
import { useAuth } from '@/features/auth/context/AuthProvider'
import { useVendor } from '@/features/inventory/hooks/useVendor'
import { useOrders, useUpdateOrderStatus } from '../hooks/useOrders'
import { useOrderPolling } from '../hooks/useOrderPolling'
import { OrderCard } from '../components/OrderCard'
import { OrderDetail } from '../components/OrderDetail'
import { httpClient } from '@/lib/api/httpClient'
import type { AccountMeResponse } from '@/shared/types'

const TABS = [
  { key: 'ALL', label: 'All' },
  { key: 'NEW', label: 'New', statuses: 'CREATED' },
  { key: 'IN_PROGRESS', label: 'In Progress', statuses: 'CONFIRMED,PREPARING,PAID,SHIPPED,OUT_FOR_DELIVERY' },
  { key: 'COMPLETED', label: 'Completed', statuses: 'DELIVERED' },
  { key: 'CANCELLED', label: 'Cancelled', statuses: 'CANCELLED' },
]

export function OrdersPage() {
  const { vendorId } = useParams<{ vendorId: string }>()
  const location = useLocation()
  const { logout } = useAuth()
  const [activeTab, setActiveTab] = useState('ALL')
  const [page, setPage] = useState(1)
  const [selectedOrder, setSelectedOrder] = useState<{ id: string; type: string } | null>(null)
  const [account, setAccount] = useState<AccountMeResponse | null>(null)

  const currentTab = TABS.find((t) => t.key === activeTab)
  const statusFilter = currentTab?.statuses || undefined

  const { data, isLoading } = useOrders(vendorId || '', {
    status: statusFilter,
    page,
    limit: 20,
  })
  const { mutate: updateStatus } = useUpdateOrderStatus(vendorId || '')
  const { vendor } = useVendor(vendorId || '')
  const isShopOpen = vendor?.shopState === 'OPEN' || vendor?.shopState === 'KITCHEN_BUSY'

  useOrderPolling(vendorId || '', isShopOpen)

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const res = await httpClient.get('/vendor/account/me')
        if (res.data?.data?.details) setAccount(res.data.data.details)
      } catch {}
    }
    fetchAccount()
  }, [])

  const orders = data?.orders ?? []
  const pagination = data?.pagination

  const handleAccept = (order: { id: string; orderType: string }) => {
    const nextStatus = order.orderType === 'FOOD' ? 'CONFIRMED' : 'PAID'
    updateStatus({ type: order.orderType, orderId: order.id, status: nextStatus })
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card/90 border-b sticky top-0 z-20 shadow-sm backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Logo href={`/dashboard/${vendorId}`} />
              <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
                <Link
                  to={`/dashboard/${vendorId}`}
                  className={cn(
                    'pb-1 transition-colors border-b-2',
                    'text-muted-foreground border-transparent hover:text-foreground'
                  )}
                >
                  Dashboard
                </Link>
                <Link
                  to={`/inventory/${vendorId}`}
                  className={cn(
                    'pb-1 transition-colors border-b-2',
                    'text-muted-foreground border-transparent hover:text-foreground'
                  )}
                >
                  Inventory
                </Link>
                <Link
                  to={`/orders/${vendorId}`}
                  className={cn(
                    'pb-1 transition-colors border-b-2',
                    location.pathname === `/orders/${vendorId}`
                      ? 'text-foreground border-foreground'
                      : 'text-muted-foreground border-transparent hover:text-foreground'
                  )}
                >
                  Orders
                </Link>
              </nav>
            </div>

            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon">
                <Bell className="w-5 h-5" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{account?.name || 'Account'}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{account?.name}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={`/dashboard/${vendorId}/profile`}><User className="w-4 h-4 mr-2" />Profile</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => logout()} className="text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Orders</h1>
            <p className="text-sm text-muted-foreground">Manage and track customer orders</p>
          </div>
          {pagination && (
            <Badge variant="outline" className="text-sm">
              {pagination.total} total
            </Badge>
          )}
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {TABS.map((tab) => (
            <Button
              key={tab.key}
              variant={activeTab === tab.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setActiveTab(tab.key); setPage(1) }}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-1">No orders found</h3>
            <p className="text-sm text-muted-foreground">
              {activeTab === 'ALL' ? 'Orders will appear here when customers place them.' : `No ${currentTab?.label.toLowerCase()} orders.`}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {orders.map((order: { id: string; orderType: string; status: string; [key: string]: unknown }) => (
                <OrderCard
                  key={order.id}
                  order={order as Parameters<typeof OrderCard>[0]['order']}
                  onViewDetail={() => setSelectedOrder({ id: order.id, type: order.orderType })}
                  onAccept={order.status === 'CREATED' ? () => handleAccept(order) : undefined}
                />
              ))}
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedOrder && (
        <OrderDetail
          vendorId={vendorId || ''}
          orderType={selectedOrder.type}
          orderId={selectedOrder.id}
          open={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </div>
  )
}
